import { createSelector } from "@reduxjs/toolkit";
import { getModHandler } from "@src/store/slices/experienceBuilder/common/utils/modHandlerRegistry";
import { hasModChanges } from "@src/store/slices/experienceBuilder/common/utils/hasModChanges";
import { experienceBuilderSlice } from "@src/store/slices/experienceBuilder/slice";
import { AiEmbeddedMod } from "@src/store/slices/experienceBuilder/types";
import { selectedVariations } from "@src/store/slices/variations";

/**
 * Mods grouped by variationId, enriched with per-mod status (new / changed /
 * unchanged) so the AI can reason about which entries are its own in-session
 * work vs. the merchant's pre-existing saved state.
 *
 * Each mod is converted through its handler's `toAiFormat` — the same
 * conversion `update_mods` write-back uses in reverse — rather than sent as
 * the raw internal Redux object. This strips frontend-only meta
 * (`category`, `groupId`, `nodes`, `tab`, `createdAtUrl`, `source`,
 * `variationId` — grouping is structural via the Record key here) and lets
 * a handler translate its mod into a different AI-visible type entirely
 * (`layoutModHandler` presents `layout` mods as `text`). Without this, the
 * AI never sees a consistent, schema-true shape for any mod type — just
 * whatever's in Redux, inconsistently — which is a real source of
 * fabricated fields (seen in prod: an echoed `layout` mod carrying
 * `text`-only fields it never had).
 *
 * Every variation in `selectedVariations(state)` is present as a key, even
 * when empty, so the AI sees the full set of available groups without
 * re-reading `experience.variations`.
 */
export const getAiMods = createSelector(
  experienceBuilderSlice.selectors.getMods,
  experienceBuilderSlice.selectors.getInitialMods,
  selectedVariations,
  (mods, initialMods, variations): Record<string, AiEmbeddedMod[]> => {
    const grouped: Record<string, AiEmbeddedMod[]> = {};
    for (const variation of variations) {
      grouped[variation.id] = [];
    }
    for (const mod of mods) {
      const bucket = grouped[mod.variationId];
      if (!bucket) {
        // Orphan: mod references a variation no longer in the experience.
        // Skip it so the AI's context stays consistent with the variation
        // list it sees. (Defensive — should be rare; deleted-variation
        // cleanup is the source of truth elsewhere.)
        continue;
      }
      const initial = initialMods.find((m) => m.id === mod.id);
      const status = !initial
        ? "new"
        : hasModChanges(initial, mod)
          ? "changed"
          : "unchanged";
      const aiMod = getModHandler(mod).toAiFormat(mod);
      bucket.push({ ...aiMod, status } as AiEmbeddedMod);
    }
    return grouped;
  }
);
